import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import styles from "../../../styles/__labTechnicianManagement.module.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { IoAddCircleOutline } from "react-icons/io5";
import LabDialogAddEditTechnical from "../../../components/lab-dialog-AddEditTechnical/LabDialogAddEditTechnical";
import LabDialogViewTechnical from "../../../components/lab-dialog-ViewTechnical/LabDialogViewTechnical";
import PhysioDashboardLayout from "../../../components/physio-dashboard-layout/PhysioDashboardLayout";
import { fetchTherapists } from "../../../reducer/therapistManagement/therapistManagementReducer";

const PhysioTherapyManagement = ({ onLogout, onNavigateToDashboard }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { list: therapists, pagination, isLoading } = useSelector((state) => state.therapistManagement);

  const [activeTab, setActiveTab] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 10;

  // Dialog state
  const [openDialogAddEdit, setOpenDialogAddEdit] = useState(false);
  const [dialogType, setDialogType] = useState("add");
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [openDialogView, setOpenDialogView] = useState(false);

  const totalPages = pagination?.totalPages || 1;

  // Fetch therapists from API
  const loadTherapists = (page = currentPage, status = activeTab, search = searchTerm) => {
    dispatch(fetchTherapists({ search, status, page, limit: itemsPerPage }));
  };

  useEffect(() => {
    loadTherapists(1);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    loadTherapists(1, tab, searchTerm);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadTherapists(1, activeTab, searchTerm);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setActiveTab("All");
    setCurrentPage(1);
    loadTherapists(1, "All", "");
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    loadTherapists(page);
  };

  const handleNavigate = (page) => {
    if (page === "dashboard") onNavigateToDashboard();
  };

  const handleAddNew = () => {
    setDialogType("add");
    setSelectedTherapist(null);
    setOpenDialogAddEdit(true);
  };

  const handleView = (therapist) => {
    setSelectedTherapist(therapist);
    setOpenDialogView(true);
  };

  const handleEditFromView = (therapist) => {
    setOpenDialogView(false);
    setDialogType("edit");
    setSelectedTherapist(therapist);
    setOpenDialogAddEdit(true);
  };

  const handleDialogSuccess = () => {
    loadTherapists(currentPage, activeTab, searchTerm);
  };

  const getStatusColor = (availability) => {
    return availability === "Available" ? "status-payment" : "status-red";
  };

  // "Available" and "Busy" tabs hidden per requirement — only "All" remains.
  const orderManagementTabs = ["All"];

  return (
    <PhysioDashboardLayout
      currentPage="physio_therapy_management"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      <LabDialogAddEditTechnical
        open={openDialogAddEdit}
        onClose={() => setOpenDialogAddEdit(false)}
        type={dialogType}
        therapistData={selectedTherapist}
        onSuccess={handleDialogSuccess}
      />
      <LabDialogViewTechnical
        open={openDialogView}
        onClose={() => setOpenDialogView(false)}
        therapistData={selectedTherapist}
        onEdit={handleEditFromView}
      />

      <>
        <div className={styles["apt-page-header"]}>
          <h1>Therapy Management</h1>
          <button className={styles["apt-register-btn"]} onClick={handleAddNew}>
            <IoAddCircleOutline /> Add New Therapist
          </button>
        </div>
        <div className={styles["apt-appointments-section"]}>
          {/* Filters */}
          <div className={styles["apt-filters"]}>
            <div className={styles["apt-filter-group"]}>
              <div className={styles["apt-search-wrapper"]}>
                <Search className={styles["apt-search-icon"]} />
                <input
                  type="text"
                  placeholder="Therapist name, mobile number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className={styles["apt-search-input"]}
                />
              </div>
              <button className={styles["apt-reset-btn"]} onClick={resetFilters}>
                Clear Search
              </button>
            </div>
          </div>

          <div className={styles["apt-appointment-tabs"]}>
            {orderManagementTabs.map((tabItem, idx) => (
              <button
                key={idx}
                className={`${styles["apt-tab"]} ${activeTab === tabItem ? styles["active"] : ""}`}
                onClick={() => handleTabChange(tabItem)}
              >
                {tabItem}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className={styles["apt-appointments-table"]}>
            <table>
              <thead>
                <tr>
                  <th>Therapist Name</th>
                  <th>Gender</th>
                  <th>Current Assignment</th>
                  <th>Mobile Number</th>
                  <th>Current Availability</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan="6" style={{ textAlign: "center", padding: "24px" }}>Loading...</td></tr>
                ) : therapists.length === 0 ? (
                  <tr><td colSpan="6" style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>No therapists found</td></tr>
                ) : (
                  therapists.map((item, idx) => (
                    <tr key={item.id || idx}>
                      <td>{item.name}</td>
                      <td>{item.gender}</td>
                      <td>{item.current_assignment || "Not Assigned"}</td>
                      <td>{item.mobile_number}</td>
                      <td>
                        <span className={`${styles["db-status"]} ${styles[getStatusColor(item.current_availability)]}`}>
                          {item.current_availability}
                        </span>
                      </td>
                      <td>
                        <button
                          className={styles["apt-action-btn"]}
                          title="View"
                          onClick={() => handleView(item)}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M12 5C5 5 2 12 2 12s3 7 10 7 10-7 10-7-3-7-10-7z" stroke="#155DFC" strokeWidth="1.5" />
                            <circle cx="12" cy="12" r="3" stroke="#155DFC" strokeWidth="1.5" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className={styles["apt-pagination"]}>
            <div className={styles["apt-pagination-info"]}>
              Displaying Results {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, pagination?.total || 0)} Out of {pagination?.total || 0}
            </div>
            <div className={styles["apt-pagination-controls"]}>
              <button
                className={`${styles["apt-pagination-btn"]} ${currentPage === 1 ? styles["disabled"] : ""}`}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous Page
              </button>
              <div className={styles["apt-page-numbers"]}>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    className={`${styles["apt-page-btn"]} ${currentPage === i + 1 ? styles["active"] : ""}`}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                className={`${styles["apt-pagination-btn"]} ${styles["apt-next-btn"]} ${currentPage === totalPages ? styles["disabled"] : ""}`}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next Page
              </button>
            </div>
          </div>
        </div>
      </>
    </PhysioDashboardLayout>
  );
};

export default PhysioTherapyManagement;
